import { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import {
  fromYmd,
  computeShipDate,
  toYmd,
  formatHuman,
  buildAllowedDateSet,
  lookupShipDate,
} from "./datesHelper";
import type { QivoraDate } from "@/api/qivoraClient";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  value?: string; // "YYYY-MM-DD"
  onChange?: (x: { deliveryDate: string; shipDate: string }) => void;
  qivoraDates?: QivoraDate[] | null;
  loading?: boolean;
  deliveryDayNames?: string[];
  cutoffHours?: number;
  /** Hide the confirmation rows (the parent shows the red invalid-selection strip instead). */
  suppressFooter?: boolean;
};

/** Format a list of day names for display: ["Tuesday", "Thursday"] → "Tuesdays & Thursdays" */
function formatDayNamesList(names: string[]): string {
  if (names.length === 0) return "";
  const pluralized = names.map((n) => n + "s");
  if (pluralized.length === 1) return pluralized[0];
  if (pluralized.length === 2) return `${pluralized[0]} & ${pluralized[1]}`;
  return pluralized.slice(0, -1).join(", ") + " & " + pluralized[pluralized.length - 1];
}
/** "Friday, Sep 25, 2026" — matches mockup spec exactly. */
function formatConfirmedDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Dark circular checkmark used for the delivery-info bullet rows (Figma 16v2). */
function InfoCheckIcon() {
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: QUIZ.color.brown,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: "2px",
      }}
    >
      <CheckIcon sx={{ fontSize: 13, color: QUIZ.color.green }} />
    </Box>
  );
}

/**
 * Figma 16v2 calendar: single continuous white rounded card — calendar,
 * confirmed-date line, and the delivery-info notice all sit inside one
 * card with no gaps between sections (matches mockup, no dark backgrounds).
 */
export function PawsDayPicker({ value, onChange, qivoraDates, loading, deliveryDayNames, cutoffHours, suppressFooter }: Props) {
  const [localSelected, setLocalSelected] = useState<Date | undefined>(() =>
    fromYmd(value)
  );

  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  void nowTick;

  // When Qivora dates are provided, use them for allowed/disabled logic
  const allowedSet = useMemo(
    () => (qivoraDates ? buildAllowedDateSet(qivoraDates) : null),
    [qivoraDates]
  );

  const isDisabled = (date: Date) => {
    if (allowedSet) {
      return !allowedSet.has(toYmd(date));
    }
    // No API data — disable all dates (don't fall back to hardcoded rules)
    return true;
  };

  const nextAvailable = useMemo(() => {
    if (qivoraDates?.length) {
      return fromYmd(qivoraDates[0].date);
    }
    return undefined;
  }, [qivoraDates]);

  // Sync local state with parent-controlled value
  useEffect(() => {
    const v = fromYmd(value);
    setLocalSelected(v);
  }, [value]);

  // Displayed month is CONTROLLED so the calendar always opens on (and jumps
  // to) the month of the selected or first-available date. With the previous
  // uncontrolled `defaultMonth`, mounting near month end while dates were
  // still loading left the calendar stuck on the current month even though
  // every available date was in the next one — users had to notice and page
  // forward themselves. The ref stops the snap re-firing while a user is
  // freely browsing months: we only jump when the target month actually
  // changes (initial load, auto-select, or a new selection).
  const [month, setMonth] = useState<Date | undefined>(() => localSelected ?? nextAvailable);
  const lastMonthSnapRef = useRef<string | null>(null);
  useEffect(() => {
    const target = localSelected ?? nextAvailable;
    if (!target) return;
    const key = `${target.getFullYear()}-${target.getMonth()}`;
    if (lastMonthSnapRef.current === key) return;
    lastMonthSnapRef.current = key;
    setMonth(new Date(target.getFullYear(), target.getMonth(), 1));
  }, [localSelected, nextAvailable]);

  const shipDate = useMemo(() => {
    if (!localSelected) return null;
    if (qivoraDates) {
      const apiShip = lookupShipDate(qivoraDates, toYmd(localSelected));
      if (apiShip) return fromYmd(apiShip) ?? computeShipDate(localSelected);
    }
    return computeShipDate(localSelected);
  }, [localSelected, qivoraDates]);

  const dayNamesDisplay = formatDayNamesList(deliveryDayNames ?? []);
  const cutoffDisplay = cutoffHours ?? 36;

  return (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>

    {/* Calendar card — calendar + confirmed-date line only */}
    <Box
      sx={{
        background: QUIZ.color.white,
        borderRadius: "14px",
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Calendar */}
      <Box
        sx={{
          pb: "10px",
          position: "relative",

          "& .rdp": {
            "--rdp-accent-color": QUIZ.color.blue,
            "--rdp-accent-background-color": QUIZ.color.blue,
            "--rdp-day-height": "38px",
            "--rdp-day-width": "44px",
            fontFamily: QUIZ.font.body,
            margin: 0,
            width: "100%",
          },
          "& .rdp-months, & .rdp-month": {
            width: "100%",
            maxWidth: "100%",
          },
          // Light-blue month header band
          "& .rdp-month_caption": {
            background: QUIZ.color.blue,
            justifyContent: "center",
            padding: "13px 0",
            fontWeight: 700,
            color: QUIZ.color.ink,
            fontSize: "1rem",
          },
          "& .rdp-nav": {
            position: "absolute",
            top: "4px",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            px: "10px",
            zIndex: 1,
          },
          "& .rdp-button_previous, & .rdp-button_next": {
            color: QUIZ.color.ink,
            borderRadius: "8px",
            "&:hover": { background: "rgba(255,255,255,0.35)" },
          },
          "& .rdp-chevron": {
            fill: QUIZ.color.ink,
          },
          "& .rdp-month_grid": {
            width: "calc(100% - 24px)",
            margin: "10px auto 0",
          },
          "& .rdp-weekday": {
            color: QUIZ.color.statLabel,
            fontWeight: 600,
            fontSize: "0.82rem",
          },
          "& .rdp-day": {
            borderRadius: "10px",
            transition: "all 0.15s ease",
            textAlign: "center",
          },
          "& .rdp-day:not([disabled]):hover": {
            background: "#e9f8ff",
          },
          "& .rdp-day_button": {
            fontWeight: 600,
            fontSize: "0.9rem",
            color: QUIZ.color.ink,
            margin: "0 auto",
          },
          "& .rdp-selected .rdp-day_button": {
            background: QUIZ.color.blue,
            border: "none",
            color: QUIZ.color.ink,
            fontWeight: 700,
            borderRadius: "10px",
          },
          "& .rdp-today .rdp-day_button": {
            border: "none",
            fontWeight: 600,
          },
          "& .rdp-disabled .rdp-day_button": {
            color: "#c8c2bb",
            textDecoration: "none",
            opacity: 1,
          },
          "& .rdp-outside .rdp-day_button": {
            color: "#d5d0c9",
          },
        }}
      >
        <DayPicker
          animate
          mode="single"
          selected={localSelected}
          month={month}
          onMonthChange={setMonth}
          onSelect={(date) => {
            setLocalSelected(date);
            if (!date) return;
            const ymd = toYmd(date);
            const apiShip = qivoraDates ? lookupShipDate(qivoraDates, ymd) : undefined;
            const ship = apiShip ?? toYmd(computeShipDate(date));
            onChange?.({ deliveryDate: ymd, shipDate: ship });
          }}
          disabled={isDisabled}
        />
      </Box>

      {/* Confirmed-date line ONLY */}
      {!suppressFooter && (
        <Box sx={{ px: "18px", pb: "18px" }}>
          {localSelected ? (
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: "12.5px", md: "17.5px" },
                lineHeight: 1.5,
                color: QUIZ.color.ink,
              }}
            >
              <strong>{formatConfirmedDate(localSelected)}.</strong>{" "}
              {dayNamesDisplay
                ? `Deliveries run on ${dayNamesDisplay}.`
                : "Deliveries run on Tuesdays and Fridays."}
            </Typography>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              {dayNamesDisplay ? (
                <Typography sx={{ fontSize: "0.9rem", color: QUIZ.color.ink, mb: 1, fontFamily: QUIZ.font.body }}>
                  Delivery days are <strong>{dayNamesDisplay}</strong>
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: QUIZ.color.ink, mb: 1, fontFamily: QUIZ.font.body }}>
                  {loading ? "Loading delivery dates..." : "Select a delivery date"}
                </Typography>
              )}
              <Typography sx={{ fontSize: "0.8rem", color: QUIZ.color.statLabel, fontFamily: QUIZ.font.body }}>
                Must be booked at least <strong>{cutoffDisplay} hours</strong> in advance
              </Typography>
              {nextAvailable && (
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: "0.85rem",
                    color: QUIZ.color.brown,
                    fontWeight: 600,
                    fontFamily: QUIZ.font.body,
                  }}
                >
                  Next available: {formatHuman(nextAvailable)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>

    {/* Standalone reassurance card — One-hour slot email + 48-hour frozen note (Issue: Page D, Item #3) */}
    {!suppressFooter && (
      <Box
        sx={{
          background: QUIZ.color.cream2,
          borderRadius: "14px",
          p: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <InfoCheckIcon />
          <Typography
            sx={{
              fontFamily: QUIZ.font.body,
              fontSize: { xs: "12.5px", md: "17.5px" },
              lineHeight: 1.4,
              color: QUIZ.color.ink,
            }}
          >
            <strong>You'll receive an email with a one-hour slot</strong> on the morning of delivery.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <InfoCheckIcon />
          <Typography
            sx={{
              fontFamily: QUIZ.font.body,
              fontSize: { xs: "12.5px", md: "17.5px" },
              lineHeight: 1.4,  
              color: QUIZ.color.ink,
            }}
          >
            <strong>Not in?</strong> Your box stays frozen for up to 48 hours in its insulated packaging, so the doorstep or a neighbour is fine.
          </Typography>
        </Box>
      </Box>
    )}
  </Box>
);
}